import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { AreaService } from '../area.service';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { AreaModel } from '../area.model';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    AreaSelectParentAreaRightsComponent,
    IAreaSelectParentAreaRights,
} from '../area-select-parent-area-rights/area-select-parent-area-rights.component';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { IFiTimeZone } from '@fi-shared/fi-shared-services/fi-time-zones.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { EditTranslationComponent } from '@fi-app/components/edit-translation/edit-translation.component';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { AvailableDateFormats, DefaultFormatDateArgs, fiGetUserPreferredDateFormat, isDefined } from '@fi-shared/utils';
import { ExtendSharingService } from '@fi-app/components/extend-sharing/extend-sharing.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-area-edit',
    templateUrl: './area-edit.component.html',
    styleUrls: ['./area-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-area-edit' },
    standalone: false,
})
export class AreaEditComponent implements OnInit {
    ParentAreas: AreaModel[];
    @Input('area') Area: AreaModel;
    @Input() countries: ICountryModel[];
    @Input() timeZones: IFiTimeZone[];
    @ViewChild('editTranslationComponent') editTranslationComponent: EditTranslationComponent;
    areaToCopyAccessRightsFrom: number = 0;
    EditTabs: EditTabsModel;
    mainAreaListData = new DropdownSettingsModelV2<AreaModel>({
        filterBy: 'Name',
    });
    canSetParentArea: boolean = false;
    moreMenuItems: MoreMenuMobileSettingsModel;

    constructor(
        private objectTitleService: ObjectTitleService,
        private areaService: AreaService,
        private appTextService: AppTextService,
        private contentLanguagesService: ContentLanguagesService,
        private fiLoadingService: LoadingService,
        private statisticService: StatisticService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private fiFormatDateService: FiFormatDateService,
        private extendedSharingService: ExtendSharingService,
    ) {}

    ngOnInit() {
        this.setHeaderData();
        this.setListDataSettings();
    }

    private setHeaderData() {
        this.EditTabs = new EditTabsModel(
            this.objectTitleService.GetTitle(this.Area.Id, this.Area.Name, ObjectTypes.Area),
        );
        this.EditTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );

        this.moreMenuItems = new MoreMenuMobileSettingsModel();
        this.moreMenuItems.AddListItem(this.appTextService.get('common.select_lang'), () => {
            this.openSelectLanguageDialog();
        });
    }

    private openSelectLanguageDialog() {
        this.contentLanguagesService
            .openSelectLanguageDialog(this.editTranslationComponent.ActiveItem)
            .onYesButtonClick((result: number) => {
                this.editTranslationComponent.ActiveItem = result;
            });
    }

    private setListDataSettings() {
        this.ParentAreas = [];

        // fill parent areas
        this.areaService.GetLightWeightHieracialAreas().then((parentAreas: AreaModel[]) => {
            this.canSetParentArea = true;
            this.ParentAreas = parentAreas;
            const dataSet: AreaModel[] = [];

            this.ParentAreas.forEach((parentArea: AreaModel) => {
                if (this.Area.Id !== parentArea.Id) {
                    dataSet.push(parentArea);
                } else if (parentArea.ChildItems.length) {
                    // current area has children, can't change main area
                    this.canSetParentArea = false;
                }
            });
            this.mainAreaListData.setDataSet(dataSet);
            //this.Area.TimeZoneName = undefined;
        });

        const getModelData = () => {
            return {
                area: this.Area,
            };
        };

        this.saveChangesWarningService.start(getModelData);
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.Area, this.Area.Id);

        if (!isDefined(this.Area.PreferredDateFormat))
            this.Area.PreferredDateFormat = fiGetUserPreferredDateFormat(
                DefaultFormatDateArgs.applicationLanguageType,
            ).locale;
    }

    private saveArea() {
        const isNewArea: boolean = !this.Area.Id;
        let areaNewId: number;
        const savePromise: Promise<void> = new Promise<void>((resolve, reject) => {
            this.areaService.SaveArea(this.Area).then((data: number) => {
                // if not new Area or invalid data
                areaNewId = data;
                if (this.Area.Id || !data || !this.areaToCopyAccessRightsFrom) {
                    resolve();
                } else {
                    this.areaService
                        .UpdateAreaAccessRights({
                            SourceAreaId: this.areaToCopyAccessRightsFrom,
                            TargetAreaId: data,
                        })
                        .then(resolve, reject);
                }
            }, reject);
        });
        savePromise.then(() => {
            this.saveChangesWarningService.stop();
            if (isNewArea) {
                this.showSetExtendedSharingSettings(areaNewId);
                return;
            }
            this.stateService.go(FiStateNames.Area.List);
        });

        this.fiLoadingService.trackPromise(savePromise);
    }

    private showSetExtendedSharingSettings(areaId: number) {
        this.extendedSharingService
            .getExtendedSharingRecommendationDialog(ObjectTypes.Area)
            .onNoButtonClick(() => this.stateService.go(FiStateNames.Area.List))
            .onYesButtonClick(() => this.extendedSharingService.openExtendedSharing(areaId, ObjectTypes.Area));
    }

    save() {
        new Promise<void>((resolve, reject) => {
            if (!this.Area.ParentId) {
                resolve();
                return;
            }
            const parentArea: AreaModel = this.ParentAreas.find((area: AreaModel) => area.Id === this.Area.ParentId);
            if (!parentArea || parentArea.ChildItems.length) {
                resolve();
                return;
            }

            const defaultContentLanguageIdx: number = this.contentLanguagesService.GetDefaultLanguageIdx() || 0;
            const areaName: string = this.Area.Names[defaultContentLanguageIdx].Value;

            this.fiDialogsService
                .confirm({
                    messageText: this.appTextService.get('area.turn_into_main_area_message', {
                        '{0}': parentArea.Name,
                        '{1}': areaName,
                    }),
                    headerText: this.appTextService.get('area.turn_into_main_area_header', { '{0}': parentArea.Name }),
                    yesButtonText: this.appTextService.get('common.proceed'),
                })
                .onYesButtonClick(resolve)
                .onNoButtonClick(reject);
        }).then(() => {
            this.saveArea();
            /* note: commented until sharing sub areas feature is resolved
               https://chainformation.atlassian.net/browse/FI-5248?focusedCommentId=51493

            if (!this.Area.Id && this.Area.ParentId) {
                this.checkParentAreaAccessRights(this.Area.ParentId).then(() => this.saveArea());
            } else {
                this.saveArea();
            }
             */
        });
    }

    private checkParentAreaAccessRights(parentAreaId: number): Promise<void> {
        return new Promise<void>((resolve) => {
            if (!this.ParentAreas) {
                resolve();
                return;
            }

            const parentArea: AreaModel = this.ParentAreas.find(
                (parentArea: AreaModel) => parentArea.Id === parentAreaId,
            );

            if (!parentArea || !parentArea.ChildItems.length) {
                resolve();
                return;
            }

            const dialogContentData: IAreaSelectParentAreaRights = {
                items: parentArea.ChildItems,
            };

            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('area.copy_sharing_settings'),
                    yesButtonText: this.appTextService.get('common.yes_copy'),
                    noButtonText: this.appTextService.get('common.no_thanks'),
                    contentComponentRef: AreaSelectParentAreaRightsComponent,
                    contentComponentData: dialogContentData,
                    config: {
                        panelClass: 'dialog-area-select-parent-area',
                    },
                })
                .onYesButtonClick((areaToCopyAccessRightsFrom: number) => {
                    this.areaToCopyAccessRightsFrom = areaToCopyAccessRightsFrom;
                    resolve();
                })
                .onNoButtonClick(resolve);
        });
    }

    getTimeFormats() {
        return this.fiFormatDateService.timeFormas;
    }

    protected readonly AvailableDateFormats = AvailableDateFormats;
}
